/**
 * Wire codec — the option-2 boundary (see the CTO note at the top of
 * ``api/base.ts``).
 *
 * The global axios interceptor recursively camelizes EVERY JSON
 * response, which corrupts the semantic snake_case keys inside
 * ``JSONField`` payloads (a ``card_kind`` discriminator becomes
 * ``cardKind``) — and the SAME payloads arrive snake_case on the
 * non-axios SSE path, so a consumer breaks depending on which transport
 * delivered the bytes.
 *
 * This module is the systematic migration seam toward per-endpoint Zod
 * codecs:
 *
 *   1. ``{ rawResponse: true }`` on a request tells the base.ts
 *      interceptor to SKIP camelization, so ``response.data`` arrives
 *      verbatim snake_case — the SAME shape the SSE path sees.
 *   2. ``codecGet`` / ``codecPost`` set that flag and run a Zod schema
 *      over the raw payload, returning a typed, normalized result. The
 *      schema (built from ``dualKey``) is transport-agnostic: feed it
 *      the axios-raw shape OR the SSE shape and it produces the same
 *      canonical output. That kills the dual-shape bug at the root.
 *
 * This is NOT the scattered per-endpoint ``disable the interceptor``
 * the CTO note warns against — it's a single, typed, tested mechanism.
 * Endpoints migrate one at a time; the global interceptor keeps serving
 * every un-migrated (pure-DRF) endpoint unchanged.
 *
 * Canonical output is snake_case for opaque domain payloads, matching
 * the Pydantic / SSE source of truth and the existing
 * ``chat/tool-cards/schemas.ts`` convention.
 */
import type { z } from "zod";
import type { AxiosRequestConfig } from "axios";

import api from "@/api/base";

// Augment axios's config so ``rawResponse`` is a first-class, typed
// option rather than a cast-through. The base.ts response interceptor
// reads it off ``response.config``.
declare module "axios" {
  // Module augmentation MUST reuse axios's exact interface name; the
  // "shadow" of the type-only import is the mechanism, not a bug.
  // eslint-disable-next-line @typescript-eslint/no-empty-interface, @typescript-eslint/no-shadow
  interface AxiosRequestConfig {
    /**
     * When true, the base.ts response interceptor does NOT run
     * ``humps.camelizeKeys`` on this response. The caller takes
     * ownership of the wire shape (via a codec / Zod schema). Used by
     * ``codecGet`` / ``codecPost``.
     */
    rawResponse?: boolean;
  }
}

// ---------------------------------------------------------------------
// Shared wire-shape primitives. These were first written inline in
// ``chat/tool-cards/schemas.ts``; promoted here so every codec uses ONE
// implementation of the snake/camel tolerance idiom.
// ---------------------------------------------------------------------

export const isObject = (v: unknown): v is Record<string, unknown> =>
  typeof v === "object" && v !== null && !Array.isArray(v);

export const toCamel = (s: string): string =>
  s.replace(/_([a-z0-9])/g, (_, c: string) => c.toUpperCase());

/**
 * Recursively camelCase every object key in a value. Arrays are walked
 * element-wise; primitives pass through unchanged. Depth-bounded at 6
 * to defend against a (never-actually-seen) cyclic payload.
 *
 * This is the deep counterpart to ``dualKey`` — ``dualKey`` reads a
 * single field tolerant of either spelling; ``toCamelDeep`` rewrites an
 * entire canonical snake_case sub-tree into the camelCase shape FE
 * primitives consume. The two share the same ``toCamel`` word boundary,
 * so a value produced by a ``dualKey``-built codec and then run through
 * ``toCamelDeep`` lands on the exact camelCase keys component props
 * expect. Promoted here (was a hand-rolled ``camelize`` inside
 * ``toolResultDispatcher.tsx``) so there is ONE implementation.
 */
export const toCamelDeep = (input: unknown, depth = 0): unknown => {
  if (depth > 6) return input;
  if (Array.isArray(input)) {
    return input.map((v) => toCamelDeep(v, depth + 1));
  }
  if (input && typeof input === "object") {
    return Object.entries(input as Record<string, unknown>).reduce<
      Record<string, unknown>
    >((acc, [k, v]) => {
      acc[toCamel(k)] = toCamelDeep(v, depth + 1);
      return acc;
    }, {});
  }
  return input;
};

/**
 * Resolve a value by its snake_case key, tolerating a camelCase
 * spelling as a fall-back. The snake form is resolved FIRST because it
 * is the canonical BE convention; the camelized spelling only appears
 * when a payload rode the (legacy) global camelizer. A schema built on
 * ``dualKey`` therefore parses identically whether the bytes came from
 * the axios-raw path or the SSE path.
 */
export const dualKey = (
  obj: Record<string, unknown>,
  snakeKey: string,
): unknown => {
  if (snakeKey in obj) return obj[snakeKey];
  const camel = toCamel(snakeKey);
  if (camel in obj) return obj[camel];
  return undefined;
};

// Insert ``_`` before UPPERCASE letters only — NOT before digits. This
// makes ``toSnake`` the faithful inverse of ``toCamel`` (which un-snakes
// ``_[a-z0-9]`` into an uppercase letter, so the only boundaries it ever
// creates are uppercase — never a bare digit). Splitting on digits broke
// the round-trip for keys like ``limit_tco2e`` → ``limitTco2e`` →
// (old) ``limit_tco_2e`` ≠ the column key, so the persisted-thread replay
// path silently mismatched every digit-bearing key and the grid cell read
// empty (e.g. the LL97 penalty "Emissions Limit (tCO₂e)" column showed
// "—"). Now: ``limitTco2e`` → ``limit_tco2e``. (``addressLine1`` → ``1``
// adjacency remains inherently ambiguous through camelize and is left as
// the rare unavoidable edge.)
export const toSnake = (s: string): string =>
  s.replace(/([A-Z])/g, (_, c: string) => `_${c.toLowerCase()}`);

/**
 * Recursive counterpart to ``toCamelDeep`` — rewrites every object key
 * into snake_case. Used by codecs whose canonical output is snake_case
 * (the opaque-domain-payload convention, see the module header) when
 * the bytes may have arrived already-camelized via the legacy axios
 * interceptor. Running an arbitrary payload through ``toSnakeDeep``
 * collapses both spellings onto the single canonical snake shape, so a
 * payload parses identically off the SSE path (raw snake) and the
 * replay path (camelized).
 *
 * Idempotent on snake_case input: ``camera_url`` has no upper-case
 * runs, so it round-trips unchanged.
 */
export const toSnakeDeep = (input: unknown, depth = 0): unknown => {
  if (depth > 6) return input;
  if (Array.isArray(input)) {
    return input.map((v) => toSnakeDeep(v, depth + 1));
  }
  if (input && typeof input === "object") {
    return Object.entries(input as Record<string, unknown>).reduce<
      Record<string, unknown>
    >((acc, [k, v]) => {
      acc[toSnake(k)] = toSnakeDeep(v, depth + 1);
      return acc;
    }, {});
  }
  return input;
};

// ---------------------------------------------------------------------
// Codec runners
// ---------------------------------------------------------------------

/**
 * Thrown when a codec'd endpoint's response fails its Zod schema. The
 * ``issues`` carry the Zod error detail; ``url`` aids triage.
 */
export class CodecParseError extends Error {
  readonly url: string;

  readonly issues: z.ZodIssue[];

  constructor(url: string, issues: z.ZodIssue[]) {
    super(`Codec parse failed for ${url}: ${issues.length} issue(s)`);
    this.name = "CodecParseError";
    this.url = url;
    this.issues = issues;
  }
}

const logParseFailure = (url: string, issues: z.ZodIssue[]): void => {
  if (typeof import.meta !== "undefined" && import.meta.env?.DEV) {
    // eslint-disable-next-line no-console
    console.error(`[codec] ${url} failed schema:`, issues);
  }
};

/**
 * GET an endpoint with the global camelizer DISABLED, then parse the
 * raw snake_case body through ``schema``. Returns the schema's typed
 * output. Throws ``CodecParseError`` on a shape mismatch so a bad
 * deploy surfaces loudly at the boundary instead of as a downstream
 * ``Objects are not valid as a React child``.
 */
export async function codecGet<TOut>(
  url: string,
  schema: z.ZodType<TOut, z.ZodTypeDef, unknown>,
  config?: AxiosRequestConfig,
): Promise<TOut> {
  const res = await api.get(url, { ...config, rawResponse: true });
  const parsed = schema.safeParse(res.data);
  if (!parsed.success) {
    logParseFailure(url, parsed.error.issues);
    throw new CodecParseError(url, parsed.error.issues);
  }
  return parsed.data;
}

/**
 * POST a body and parse the raw snake_case response through ``schema``.
 * The request body still rides the request interceptor's
 * ``decamelizeKeys`` (FE authors in camelCase, BE reads snake) — only
 * the RESPONSE camelization is skipped. If you need a raw request body
 * too, pass ``transformRequest`` to bypass it for that call.
 */
export async function codecPost<TOut>(
  url: string,
  body: unknown,
  schema: z.ZodType<TOut, z.ZodTypeDef, unknown>,
  config?: AxiosRequestConfig,
): Promise<TOut> {
  const res = await api.post(url, body, { ...config, rawResponse: true });
  const parsed = schema.safeParse(res.data);
  if (!parsed.success) {
    logParseFailure(url, parsed.error.issues);
    throw new CodecParseError(url, parsed.error.issues);
  }
  return parsed.data;
}
